import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, CardTitle, Col, Label, Row } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "formik"
import { useNavigate } from "react-router-dom"
import { loyaltyPointInfoSchema } from "components/Common/validation"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"

import Spinners from "components/Shared/Spinners"

import { showAlert } from "store/reducers/alertReducer"
import {
  getGlobalSettingByType,
  updateGlobalSettingByType,
} from "store/actions/globalSettingAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  requiredPointsForOneSAR: null,
  minLimitOfPointRedemtion: null,
  pointValidityInDays: null,
  earningPointInMinutes: 0,
  orderStatusForLoyaltyCredit: null,
  requiredCollectPointsForOneSAR: null,
}

const LoyaltyPointInfo = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Loyalty Settings | Namq")

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { loading, isLoading, loyaltyInfo } = useSelector(
    state => state.GlobalSettings
  )

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      20,
      "loyalty_1",
      accountType
    )

    const isPermissionEdit = isPermissionEnabled(
      userRole,
      20,
      "loyalty_3",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole])

  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    fetchGlobalSettings()
  }, [])

  useEffect(() => {
    if (loyaltyInfo) {
      if (loyaltyInfo && loyaltyInfo.loyaltyInfo) {
        const {
          requiredPointsForOneSAR,
          minLimitOfPointRedemtion,
          pointValidityInDays,
          earningPointInMinutes,
          requiredCollectPointsForOneSAR,
          orderStatusForLoyaltyCredit,
        } = loyaltyInfo?.loyaltyInfo

        setFormValues({
          requiredPointsForOneSAR,
          minLimitOfPointRedemtion,
          pointValidityInDays,
          earningPointInMinutes,
          requiredCollectPointsForOneSAR,
          orderStatusForLoyaltyCredit,
        })
      }
    }
  }, [loyaltyInfo])

  const fetchGlobalSettings = () => {
    const payload = {
      type: "loyaltyInfo",
    }
    dispatch(getGlobalSettingByType(payload))
  }

  const handleOnSubmit = values => {
    const {
      requiredPointsForOneSAR,
      minLimitOfPointRedemtion,
      pointValidityInDays,
      earningPointInMinutes,
      requiredCollectPointsForOneSAR,
      orderStatusForLoyaltyCredit,
    } = values

    const payload = {
      id: loyaltyInfo._id,
      type: "loyaltyInfo",
      loyaltyInfo: {
        requiredPointsForOneSAR,
        minLimitOfPointRedemtion,
        pointValidityInDays,
        earningPointInMinutes: earningPointInMinutes || 0, //default 0
        requiredCollectPointsForOneSAR,
        orderStatusForLoyaltyCredit: Number(orderStatusForLoyaltyCredit),
      },
    }
    dispatch(updateGlobalSettingByType(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  if (loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />

        <Formik
          initialValues={formValues}
          enableReinitialize={true}
          validationSchema={loyaltyPointInfoSchema}
          onSubmit={handleOnSubmit}
        >
          {({ values, errors, touched }) => (
            <Row>
              <Col lg={6}>
                <Card>
                  <CardBody>
                    <CardTitle className="d-flex align-items-center">
                      {t("Loyalty Settings")}
                    </CardTitle>

                    <Form>
                      <Row className="mb-4">
                        <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="requiredPointsForOneSAR"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Redeem Points On 1 SAR")}
                            </Label>
                            <Field
                              name="requiredPointsForOneSAR"
                              type="number"
                              id="requiredPointsForOneSAR"
                              placeholder={t("Redeem Points On 1 SAR")}
                              pattern="[0-9]*"
                              className={`form-control ${
                                touched.requiredPointsForOneSAR &&
                                errors.requiredPointsForOneSAR
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="requiredPointsForOneSAR" />
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="minLimitOfPointRedemtion"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Minimum Point Redemption Limit")}
                            </Label>
                            <Field
                              name="minLimitOfPointRedemtion"
                              type="number"
                              id="minLimitOfPointRedemtion"
                              placeholder={t("Minimum Point Redemption Limit")}
                              pattern="[0-9]*"
                              className={`form-control ${
                                touched.minLimitOfPointRedemtion &&
                                errors.minLimitOfPointRedemtion
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="minLimitOfPointRedemtion" />
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="pointValidityInDays"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Points Validity(In Days)")}
                            </Label>
                            <Field
                              name="pointValidityInDays"
                              type="number"
                              id="pointValidityInDays"
                              placeholder={t("Points Validity(In Days)")}
                              pattern="[0-9]*"
                              className={`form-control ${
                                touched.pointValidityInDays &&
                                errors.pointValidityInDays
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="pointValidityInDays" />
                          </div>
                        </Col>

                        <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="orderStatusForLoyaltyCredit"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Order Status For Loyalty Credit")}
                            </Label>
                            <Field
                              as="select"
                              name="orderStatusForLoyaltyCredit"
                              id="orderStatusForLoyaltyCredit"
                            >
                              <option hidden>
                                {t("Select Order Status For Loyalty Credit")}
                              </option>
                              <option value={1}>{t("Sent To Branch")}</option>
                              <option value={2}>{t("Start Preparing")}</option>
                              <option value={3}>{t("Prepared")}</option>
                              <option value={4}>{t("Collected")}</option>
                            </Field>
                            <TextError name="orderStatusForLoyaltyCredit" />
                          </div>
                        </Col>

                        {/* <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="earningPointInMinutes"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Credit Window (in minutes)")}
                            </Label>
                            <Field
                              name="earningPointInMinutes"
                              type="number"
                              id="earningPointInMinutes"
                              placeholder={t("Credit Window (in minutes)")}
                              pattern="[0-9]*"
                              className={`form-control ${
                                touched.earningPointInMinutes &&
                                errors.earningPointInMinutes
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError
                              name="earningPointInMinutes"
                              
                            />
                          </div>
                        </Col> */}

                        <Col md={12}>
                          <div className="form_field">
                            <Label
                              htmlFor="requiredCollectPointsForOneSAR"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>
                              {t("Collect Points On 1 SAR")}
                            </Label>
                            <Field
                              name="requiredCollectPointsForOneSAR"
                              type="number"
                              id="requiredCollectPointsForOneSAR"
                              placeholder={t("Collect Points On 1 SAR")}
                              pattern="[0-9]*"
                              className={`form-control ${
                                touched.requiredCollectPointsForOneSAR &&
                                errors.requiredCollectPointsForOneSAR
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="requiredCollectPointsForOneSAR" />
                          </div>
                        </Col>

                        <Col lg={12}>
                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              type="button"
                              color="secondary"
                              onClick={() => navigate(-1)}
                            >
                              {t("Cancel")}
                            </Button>
                            {usersPermissions.edit && (
                              <Button
                                className="mx-2"
                                type="submit"
                                color="success"
                                disabled={isLoading}
                              >
                                {t("Save")}
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={6} className="">
                <Card className="card_100_24">
                  <CardBody>
                    <CardTitle>{t("Loyalty Settings Hints/Notes")}</CardTitle>
                    <div className="hints mt-3">
                      <div className="single_hint">
                        <p className="fw-bold">
                          1. {t("Redeem Points On 1 SAR")}
                        </p>
                        <p>
                          {t(
                            "Once the customer accumulates ({{value}} points) they can receive an order discount 1 SAR.",
                            { value: values.requiredPointsForOneSAR }
                          )}
                        </p>
                      </div>
                      <div className="single_hint">
                        <p className="fw-bold">
                          2. {t("Minimum Point Redemption Limit")}
                        </p>
                        <p>
                          {t(
                            "The minimum point that can be redeemed is {{value}} points.",
                            { value: values.minLimitOfPointRedemtion }
                          )}
                        </p>
                      </div>

                      <div className="single_hint">
                        <p className="fw-bold">
                          3. {t("Points Validity(In Days)")}
                        </p>
                        <p>
                          {t("Points Validity {{value}} ( In Days)", {
                            value: values.pointValidityInDays,
                          })}
                        </p>
                      </div>
                      <div className="single_hint">
                        <p className="fw-bold">
                          4.{" "}
                          {t(
                            `How does Order Status For Loyalty Credit field work?`
                          )}
                        </p>
                        <p>
                          {t(
                            "Loyalty points will be credited to the customer after the order reaches"
                          )}
                          "
                          {values.orderStatusForLoyaltyCredit == 1 &&
                            "Sent To Branch"}
                          {values.orderStatusForLoyaltyCredit == 2 &&
                            t("Start Preparing")}
                          {values.orderStatusForLoyaltyCredit == 3 &&
                            t("Prepared")}
                          {values.orderStatusForLoyaltyCredit == 4 &&
                            t("Collected")}
                          " {t("status")}.
                        </p>
                      </div>
                      {/* <div className="single_hint">
                        <p className="fw-bold">
                          5. {t("Credit Window (in minutes)")}
                        </p>
                        <p>
                          {t(
                            "The points are added after ({{value}} minutes) order status chosen above.",
                            { value: values.earningPointInMinutes }
                          )}
                        </p>
                      </div> */}
                      <div className="single_hint">
                        <p className="fw-bold">
                          5. {t("How does work Collect Points On 1 SAR field?")}
                        </p>
                        <p>
                          {t(
                            "When your customer spends (SAR 1) or more on order, they will get ({{value}} point) for each (SAR 1) spent.",
                            { value: values.requiredCollectPointsForOneSAR }
                          )}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default LoyaltyPointInfo
